import "@gdk/base/dist/js/jquery.maskedinput.geico";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import '../../styles/custom.css';
import PageHeader from "../PageHeader/PageHeader";
import { useEffect, useRef, useState } from "react";
import TooltipLabel from "../gdk/TooltipLabel";
import useUsStateOptions from "../../hooks/useUsStateOptions";
import FatcaCodeTooltipContent from "../TaxFormInputs/FatcaCodeTooltipContent";
import ExemptCodeTooltipContent from "../TaxFormInputs/ExemptCodeTooltipContent";
import ClassificationTooltipContent from "../TaxFormInputs/ClassificationTooltipContent";
import { GdkModal } from "@gdk/modal";
import { GdkSocialSecurityNumber } from "@gdk/social-security-number";
import { GdkFein } from "@gdk/fein";
import { generateGeaoCommissionDocuments } from "../../api/docusign";
import Loader, { Types } from "../gdk/Loader";
import Layout from "../Layout";
import { useNavigate } from "react-router-dom";
import useFatcaCodeOptions from "./hooks/useFatcaCodeOptions";

const validationSchema = yup.object().shape({
    name: yup
        .string()
        .required("Please enter the name of the entity/individual"),
    businessName: yup.string().optional(),
    classifications: yup
        .string()
        .required("Please select your federal tax classification"),
    llcType: yup.string().when("classifications", {
        is: (value) => value === "Limited Liability Company (LLC)",
        then: (schema) => schema.required("Please choose an LLC type"),
    }),
    otherTaxClass: yup.string().when("classifications", {
        is: (value) => value === "Other",
        then: (schema) => schema.required("Please enter your tax classification"),
    }),
    exemptCode: yup.string().optional(),
    fatcaCode: yup.string().optional(),
    address: yup
        .string()
        .max(40, "Address is too long")
        .required("Please enter your address"),
    city: yup
        .string()
        .max(40, "City is too long")
        .required("Please enter your city"),
    states: yup.string().max(2).required("Please select your state"),
    zip: yup
        .string()
        .matches(/^[\d]{5,5}$/, "Invalid Zip.")
        .required("Please enter your zip"),
    account: yup.string().optional().max(50, "Account number is too long"),
});

export default function GeaoCommissionTaxForm() {
    const { register, handleSubmit, clearErrors, setValue, getValues, setError, formState: { errors } } = useForm({
        resolver: yupResolver(validationSchema)
    });

    const [ssnLock, setSsnLock] = useState(false);
    const [feinLock, setFeinLock] = useState(false);
    const [showError, setShowError] = useState(false);
    const [llcTypeRequired, setLlcTypeRequired] = useState(false);
    const [otherTaxClassRequired, setOtherTaxClassRequired] = useState(false);
    const usStateOptions = useUsStateOptions();
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const fatcaCodeOptions = useFatcaCodeOptions();

    var ssn = useRef();

    useEffect(() => {
        ssn.current = new GdkSocialSecurityNumber({
            content: "#ssn",
        });
    }, []);

    var fein = useRef();

    useEffect(() => {
        fein.current = new GdkFein({
            content: "#fein",
        });
    }, []);

    const handleClassificationChange = (text) => {
        setLlcTypeRequired(text.target.value === "Limited Liability Company (LLC)");
        setOtherTaxClassRequired(text.target.value === "Other");
        clearErrors("classifications");
    };

    const validateSsnFein = () => {
        let ssn = getValues("ssn");
        let fein = getValues("fein");

        if (!ssn && !fein) {
            setError("ssn");
            setError("fein");
            return false;
        } else if (ssn && !fein && !ssn.match(new RegExp(/^\d{3}-?\d{2}-?\d{4}$|^•••-••-?\d{4}$/))) {
            setError("ssn");
            return false;
        } else if (fein && !ssn && !fein.match(new RegExp(/^\d{2}-?\d{7}$|^••-•••?\d{4}$/))) {
            setError("fein");
            return false;
        }
        clearErrors("fein");
        clearErrors("ssn");
        return true;
    };

    const onSubmit = async (data) => {
        if (!validateSsnFein()) {
            return;
        }

        setLoading(true);
        const requestData = {
            Name: data.name,
            BusinessName: data.businessName,
            TaxClassification: data.classifications,
            LLCType: data.llcType,
            OtherText: data.otherTaxClass,
            ExemptCode: data.exemptCode,
            ReportCode: data.fatcaCode,
            Address: data.address,
            City: data.city,
            State: data.states,
            ZipCode: data.zip,
            AccountNumber: data.account,
            SSN: ssn.current?.getSocialSecurityNumber(),
            Fein: fein.current?.getFederalIdentificationNumber(),
            Forms: ["W9"]
        };

        const success = (res) => {
            window.location.href = res.docServiceUrl;
        }

        const failure = () => {
            setLoading(false);
            setShowError(true);
        }

        generateGeaoCommissionDocuments(requestData, success, failure);
    };

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const gdkModalReference = useRef(null);
    const modalIdReference = useRef("cancel-modal");

    const onCancelConfirm = () => {
        setShowConfirmModal(false);
        navigate("/bank-info");
    }

    useEffect(() => {
        gdkModalReference.current = new GdkModal({
            content: "#" + modalIdReference.current,
            hideCloseButton: true,
            onClosed: () => {
                setShowConfirmModal(false);
            },
        });
    }, []);

    useEffect(() => {
        if (showConfirmModal === true) {
            gdkModalReference.current.show();
        }
        if (showConfirmModal === false) {
            gdkModalReference.current.hide();
        }
    }, [showConfirmModal]);

    useEffect(() => {
        // We cannot use "watch" to handle enable/disable for SSN/FEIN, so we have to lock via key-up events
        document.getElementById("fein-field").onkeyup = () => {
            if (document.getElementById("fein-field").value === "") {
                setFeinLock(false);
                setSsnLock(false);
            } else {
                setSsnLock(true);
            }
        };
        document.getElementById("ssn-input-field").onkeyup = () => {
            if (document.getElementById("ssn-input-field").value === "") {
                setFeinLock(false);
                setSsnLock(false);
            } else {
                setFeinLock(true);
            }
        };
    }, []);

    if (loading) {
        return <Loader type={Types.Section}>We are processing your form.<br />Thank you for your patience.</Loader>
    }

    return <>
        <div id="cancel-modal" className="modal" >
            <div className="modal-container" style={{ height: '25rem', width: '30%' }}>
                <h4 className="modal-headline"> Are you sure you want to cancel? </h4>
                <div className="modal-content modal-content-with-cta-bar">
                    Your information will remain unchanged
                </div>
                <div className="modal-content">
                    <div className="modal--call-to-action-bar">
                        <button data-onboarding-button-id="GeaoCommissionTaxForm:ConfirmCancel" type="button" className="btn btn--secondary btn--full-mobile btn--pull-right" onClick={onCancelConfirm}>
                            <span>Yes</span>
                        </button>
                        <button data-onboarding-button-id="GeaoCommissionTaxForm:KeepEditing" type="button" className="btn btn--secondary btn--full-mobile btn--pull-right" onClick={() => setShowConfirmModal(false)}>
                            <span>No</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <Layout>
            <PageHeader header="GEICO Commission Tax Form (W-9)" />
            <div className="container margin-top match-widths">
                <form className="form w-lg-80 center-block" onSubmit={handleSubmit(onSubmit)}>

                    {/* Error */}
                    {showError && (
                        <div id="error-message" className="alert">
                            <div className="alert--high-importance">
                                <span className="icon-alert" />
                                <ul className="alert-list">
                                    <li className="alert-content">
                                        <div className="h4">Tax Form Submission Failure</div>
                                        <p>Error submitting tax form. Please try again.</p>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* Subtext */}
                    <div>
                        <p>
                            To update your Commissions Tax form (W-9), please fill out the form below
                            Once completed, a W-9 form will be created with your information for review and approval
                            You are able to submit an updated tax form at any time in the menu options.
                        </p>
                        <p style={{ fontStyle: "italic" }}>
                            For the security of your personal information, previously entered details are not carried over to this tax form.
                        </p>
                    </div>

                    {/* Name/Entity input */}
                    <div id="name-form" className={`form-field large-text-input ${errors.name && `form-field--error`}`}>
                        <div className="tooltip-label-container">
                            <TooltipLabel
                                htmlFor="name"
                                label="Name of Entity/Individual"
                                content="For a sole proprietor or disregarded entity, enter the owner’s name in this field, and enter the business/disregarded entity’s name below."
                            />
                        </div>

                        <input
                            id="name"
                            name="name"
                            type="text"
                            data-testid="nameTest"
                            autoComplete="name"
                            size="50"
                            {...register("name")}
                            onInput={(e) =>
                                (e.target.value = ("" + e.target.value).toUpperCase())
                            }
                        />
                        {errors.name && (
                            <span className="form-message" role="alert">
                                {errors.name.message}
                            </span>
                        )}
                    </div>

                    {/* Business name input */}
                    <div id="businessName-form" className={`form-field large-text-input ${errors.businessName && `form-field--error`}`}>
                        <div>
                            <TooltipLabel
                                htmlFor="businessName"
                                label="Business Name or Disregarded Entity Name"
                                content="If you have a business name, trade name, DBA name, or disregarded entity name, enter it here."
                            />
                        </div>
                        <span id="businessNameDesciption" className="form-descriptive-copy">
                            Enter if different than Name of Entity/Individual.
                        </span>
                        <input
                            id="businessName"
                            name="businessName"
                            data-testid="businessNameTest"
                            type="text"
                            size="50"
                            {...register("businessName")}
                            onInput={(e) =>
                                (e.target.value = ("" + e.target.value).toUpperCase())
                            }
                        />
                        {errors.businessName && (
                            <span className="form-message" role="alert">
                                {errors.businessName.message}
                            </span>
                        )}
                    </div>

                    {/* Classifications input */}
                    <div id="classifications-form" className={`form-field large-text-input ${errors.classifications && `form-field--error`}`}>
                        <div>
                            <TooltipLabel
                                htmlFor="classifications"
                                label="Federal Tax Classification"
                                content={<ClassificationTooltipContent />}
                            />
                        </div>
                        <div className="select-box">
                            <select
                                id="classifications"
                                name="classifications"
                                data-testid="classificationsTest"
                                {...register("classifications")}
                                onChange={(v) => {
                                    handleClassificationChange(v);
                                }}
                            >
                                <option value="">Please Select</option>
                                <option value="Individual">
                                    Individual/Sole Proprietor
                                </option>
                                <option value="CCorporation">C Corporation</option>
                                <option value="SCorporation">S Corporation</option>
                                <option value="Partnership">Partnership</option>
                                <option value="Trust_Estate">Trust/Estate</option>
                                <option value="LLC">
                                    Limited Liability Company (LLC)
                                </option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                        {errors.classifications && (
                            <span className="form-message" role="alert">
                                {errors.classifications.message}
                            </span>
                        )}
                    </div>

                    {/* Optional LLC Type input if LLC is selected */}
                    {llcTypeRequired === true && (
                        <div id="llcType-form" className={`form-field large-text-input ${errors.llcType && `form-field--error`}`}>
                            <div className="tooltip-label-container">
                                <TooltipLabel
                                    htmlFor="llcType"
                                    label="LLC Type"
                                    content={
                                        "Select the appropriate tax classification of the LLC, unless it is a disregarded entity. " +
                                        "A disregarded entity should instead choose the appropriate tax classification of its owner."
                                    }
                                />
                            </div>
                            <div className="select-box">
                                <select
                                    id="llcType"
                                    name="llcType"
                                    data-testid="llcTypeTest"
                                    {...register("llcType")}
                                >
                                    <option value="">Please Select</option>
                                    <option value="C">C Corporation</option>
                                    <option value="S">S Corporation</option>
                                    <option value="P">Partnership</option>
                                </select>
                            </div>
                            {errors.llcType && (
                                <span className="form-message" role="alert">
                                    {errors.llcType.message}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Optional Other Tax Classification input if Other is selected */}
                    {otherTaxClassRequired === true && (
                        <div id="otherTaxClass-form" className={`form-field large-text-input ${errors.otherTaxClass && `form-field--error`}`}>
                            <div className="tooltip-label-container">
                                <TooltipLabel
                                    htmlFor="otherTaxClass"
                                    label="Other Tax Classification"
                                    content="This field should be used for classifications that are not listed such as nonprofits, governmental entities, etc."
                                />
                            </div>
                            <input
                                id="otherTaxClass"
                                name="otherTaxClass"
                                type="text"
                                style={{ width: "60%" }}
                                {...register("otherTaxClass")}
                            />
                            {errors.otherTaxClass && (
                                <span className="form-message" role="alert">
                                    {errors.otherTaxClass.message}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Exempt code can be numbers between 1 and 13 */}
                    <div id="exempt">
                        <div id="exemptCode-form" className={`form-field large-text-input ${errors.exemptCode && `form-field--error`}`}>
                            <TooltipLabel
                                htmlFor="exemptCode"
                                label="Exempt Payee Code"
                                content={<ExemptCodeTooltipContent />}
                            />
                            <span className="form-descriptive-copy " id="exemptCodedesc">
                                (Optional)
                            </span>
                            <div className="select-box">
                                <select
                                    id="exemptCode"
                                    name="exemptCode"
                                    data-testid="exemptCodeTest"
                                    {...register("exemptCode")}
                                >
                                    <option value="">Please Select</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                    <option value="5">5</option>
                                    <option value="6">6</option>
                                    <option value="7">7</option>
                                    <option value="8">8</option>
                                    <option value="9">9</option>
                                    <option value="10">10</option>
                                    <option value="11">11</option>
                                    <option value="12">12</option>
                                    <option value="13">13</option>
                                </select>
                            </div>
                            {errors.exemptCode && (
                                <span className="form-message" role="alert">
                                    {errors.exemptCode.message}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* code can be letters between A and M */}
                    <div id="fatcacode-container">
                        <div id="fatcacode-form" className={`form-field large-text-input ${errors.fatcaCode && `form-field--error`}`}>
                            <TooltipLabel
                                htmlFor="fatcaCode"
                                label="Exemption From FATCA Reporting Code"
                                content={<FatcaCodeTooltipContent />}
                            />
                            <span className="form-descriptive-copy " id="fatcacodedesc">
                                (Optional)
                            </span>
                            <div className="select-box">
                                <select
                                    id="fatcaCode"
                                    name="fatcaCode"
                                    data-testid="fatcaCodetest"
                                    {...register("fatcaCode")}
                                >
                                    <option value="">Please Select</option>
                                    {fatcaCodeOptions.map(({ value, text }) => (
                                        <option value={value} key={value}>{text}</option>
                                    ))}
                                </select>
                            </div>
                            {errors.fatcaCode && (
                                <span className="form-message" role="alert">
                                    {errors.fatcaCode.message}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Address Street 1 input */}
                    <div id="address-form" className={`form-field large-text-input ${errors.address && `form-field--error`}`}>
                        <div className="tooltip-label-container">
                            <TooltipLabel
                                htmlFor="address"
                                label="Address"
                                content="Enter your address (number, street, and apartment or suite number). This is where GEICO,
                                    as the requester of this Form W-9, will mail your information returns. If a new address is provided,
                                    there is still a chance the old address will be used until GEICO changes your address in our records."
                            />
                        </div>
                        <span id="addressdesc" className="form-descriptive-copy">
                            Number, Street, and Apartment or Suite Number
                        </span>
                        <input
                            id="address"
                            name="address"
                            type="text"
                            data-testid="addressTest"
                            autoComplete="street-address"
                            size="50"
                            {...register("address")}
                            onInput={(e) =>
                                (e.target.value = ("" + e.target.value).toUpperCase())
                            }
                        />
                        {errors.address && (
                            <span className="form-message" role="alert">
                                {errors.address.message}
                            </span>
                        )}
                    </div>

                    {/* City input */}
                    <div id="city-form" className={`form-field large-text-input ${errors.city && `form-field--error`}`}>
                        <div>
                            <label htmlFor="city" className="text label--inline">City</label>
                        </div>
                        <input
                            id="city"
                            name="city"
                            type="text"
                            data-testid="cityTest"
                            size="50"
                            {...register("city")}
                            onInput={(e) =>
                                (e.target.value = ("" + e.target.value).toUpperCase())
                            }
                        />
                        {errors.city && (
                            <span className="form-message" role="alert">
                                {errors.city.message}
                            </span>
                        )}
                    </div>

                    {/* State input */}
                    <div id="states-form" className={`form-field large-text-input ${errors.states && `form-field--error`}`}>
                        <div>
                            <label htmlFor="states" className="text label--inline">State</label>
                        </div>
                        <div className="select-box">
                            <select
                                id="states"
                                name="states"
                                data-testid="statesTest"
                                {...register("states")}
                                placeholder="Please Select"
                            >
                                <option value="">Please Select</option>
                                {usStateOptions?.map(option => (
                                    <option value={option.value} key={option.value} >{option.label}</option>
                                ))}
                            </select>
                        </div>
                        {errors.states && (
                            <span className="form-message" role="alert">
                                {errors.states.message}
                            </span>
                        )}
                    </div>

                    {/* Zip code input */}
                    <div id="zip-form" className={`form-field large-text-input ${errors.zip && `form-field--error`}`}>
                        <div>
                            <label htmlFor="zip" className="text label--inline">Zip Code</label>
                        </div>
                        <input
                            id="zip"
                            name="zip"
                            type="text"
                            data-testid="zipTest"
                            size="20"
                            {...register("zip")}
                        />
                        {errors.zip && (
                            <span className="form-message" role="alert">
                                {errors.zip.message}
                            </span>
                        )}
                    </div>

                    {/* Account number input */}
                    <div id="account-form" className={`form-field large-text-input ${errors.account && `form-field--error`}`}>
                        <label htmlFor="account" className="text label--inline">Account Numbers</label>
                        <span className="form-descriptive-copy " id="accountdesc">
                            (Optional)
                        </span>
                        <input
                            id="account"
                            name="account"
                            type="text"
                            data-testid="accountTest"
                            size="50"
                            {...register("account")}
                        />
                        {errors.account && (
                            <span className="form-message" role="alert">
                                {errors.account.message}
                            </span>
                        )}
                    </div>

                    {/* SSN input */}
                    <div id="ssn" className={`form-field social-security-number-wrapper ${errors.ssn && `form-field--error`}`}>
                        <TooltipLabel
                            htmlFor="ssn"
                            label="Social Security Number"
                            content={
                                <div tabIndex="0">
                                    At GEICO, we understand the importance of your privacy and
                                    security. The information you provide will be kept
                                    confidential and secure. It will not be sold or redistributed.
                                    To learn more about how we protect your personal information,
                                    please review our privacy &amp; security policy:{' '}
                                    <a data-onboarding-link-id="GeaoCommissionTaxForm:SsnPrivacyPolicy"
                                        href="https://media.geico.com/legal/privacy_policy.htm"
                                        className=""
                                    >
                                        Privacy Policy
                                    </a>
                                </div>
                            }
                        />
                        <input
                            data-testid="ssnTest"
                            aria-invalid="true"
                            id="ssn-input-field"
                            name="ssn-input-field"
                            type="tel"
                            className="social-security-number"
                            autoComplete="off"
                            spellCheck="false"
                            placeholder="___-__-____"
                            {...register("ssn")}
                            disabled={ssnLock}
                        />
                        <a data-onboarding-link-id="GeaoCommissionTaxForm:ToggleSsnVisibility" className="social-security-show-hide" href="/#" tabIndex="-1">
                            <span className="icon-hide" aria-label="Hide"></span>
                        </a>
                    </div>

                    <h4>OR</h4>
                    <br />

                    {/* FEIN input */}
                    <div id="fein" className={`form-field federal-employment-identification-number-wrapper ${errors.fein && `form-field--error`}`}>
                        <TooltipLabel
                            htmlFor="fein"
                            label="Federal Employment Identification Number"
                            content={
                                <div tabIndex="0">
                                    At GEICO, we understand the importance of your privacy and
                                    security. The information you provide will be kept
                                    confidential and secure, and your Federal Employment Identification
                                    Number will be encrypted. It will not be sold or
                                    redistributed. To learn more about how we protect your
                                    personal information, please review our privacy &amp; security
                                    policy:{' '}
                                    <a data-onboarding-link-id="GeaoCommissionTaxForm:FeinPrivacyPolicy"
                                        href="https://media.geico.com/legal/privacy_policy.htm"
                                        className=""
                                    >
                                        Privacy Policy
                                    </a>
                                </div>
                            }
                        />
                        <input
                            data-testid="feinTest"
                            aria-invalid="true"
                            id="fein-field"
                            name="fein-field"
                            type="tel"
                            className="federal-employment-identification-number"
                            autoComplete="off"
                            spellCheck="false"
                            placeholder="__-_______"
                            {...register("fein")}
                            disabled={feinLock}
                        />
                        <a data-onboarding-link-id="GeaoCommissionTaxForm:ToggleFeinVisibility" className="fein-show-hide" href="/#" tabIndex="-1">
                            <span className="icon-hide" aria-label="Hide"></span>
                        </a>
                    </div>

                    <div>
                        <button data-onboarding-button-id="GeaoCommissionTaxForm:CreateAndReviewW9"
                            type="submit"
                            data-testid="submit"
                            className="btn btn--primary btn--pull-right button-Banking"
                        >
                            Create and Review W-9
                        </button>
                        <button data-onboarding-button-id="GeaoCommissionTaxForm:Cancel"
                            type="button"
                            className="btn btn--destructive btn--full-mobile btn--pull-right expandable-form-cancel-btn"
                            onClick={() => setShowConfirmModal(true)}
                        >
                            <span>Cancel</span>
                        </button>
                    </div>

                </form>
            </div>
        </Layout>
    </>
}
